<div class="container mx-auto px-4 py-8">
  <!-- En-tête -->
  <div class="mb-8">
    <h1 class="text-3xl font-bold text-gray-900 mb-4">Gestion des Écarts de Stock</h1>
    <p class="text-gray-600">Gérez les écarts entre la quantité réelle en stock et la quantité enregistrée dans le système.</p>
  </div>

  <!-- Statistiques -->
  <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8" *ngIf="stats">
    <div class="bg-white rounded-lg shadow p-6">
      <div class="flex items-center">
        <div class="p-3 rounded-full bg-blue-100 text-blue-600">
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"></path>
          </svg>
        </div>
        <div class="ml-4">
          <p class="text-sm font-medium text-gray-600">Total Écarts</p>
          <p class="text-2xl font-semibold text-gray-900">{{ stats.totalDiscrepancies }}</p>
        </div>
      </div>
    </div>

    <div class="bg-white rounded-lg shadow p-6">
      <div class="flex items-center">
        <div class="p-3 rounded-full bg-yellow-100 text-yellow-600">
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
          </svg>
        </div>
        <div class="ml-4">
          <p class="text-sm font-medium text-gray-600">En Attente</p>
          <p class="text-2xl font-semibold text-gray-900">{{ stats.pendingDiscrepancies }}</p>
        </div>
      </div>
    </div>

    <div class="bg-white rounded-lg shadow p-6">
      <div class="flex items-center">
        <div class="p-3 rounded-full bg-green-100 text-green-600">
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
          </svg>
        </div>
        <div class="ml-4">
          <p class="text-sm font-medium text-gray-600">Approuvés</p>
          <p class="text-2xl font-semibold text-gray-900">{{ stats.approvedDiscrepancies }}</p>
        </div>
      </div>
    </div>

    <div class="bg-white rounded-lg shadow p-6">
      <div class="flex items-center">
        <div class="p-3 rounded-full bg-red-100 text-red-600">
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
          </svg>
        </div>
        <div class="ml-4">
          <p class="text-sm font-medium text-gray-600">Rejetés</p>
          <p class="text-2xl font-semibold text-gray-900">{{ stats.rejectedDiscrepancies }}</p>
        </div>
      </div>
    </div>
  </div>

  <!-- Boutons de création -->
  <div class="mb-6 flex space-x-4">
    <button 
      (click)="toggleCreateForm()"
      class="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200">
      <svg class="w-5 h-5 inline mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
      </svg>
      {{ showCreateForm ? 'Annuler' : 'Nouvel Écart' }}
    </button>
    
    <button 
      (click)="toggleAutoForm()"
      class="bg-green-600 hover:bg-green-700 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200">
      <svg class="w-5 h-5 inline mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z"></path>
      </svg>
      {{ showAutoForm ? 'Annuler' : 'Écart Automatique' }}
    </button>
  </div>

  <!-- Formulaire de création manuelle -->
  <div class="bg-white rounded-lg shadow p-6 mb-8" *ngIf="showCreateForm">
    <h3 class="text-lg font-semibold text-gray-900 mb-4">Nouvel Écart de Stock</h3>
    
    <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div>
        <label class="block text-sm font-medium text-gray-700 mb-2">Produit</label>
        <select 
          [(ngModel)]="createForm.productId"
          (change)="onProductChange()"
          class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500">
          <option value="0">Sélectionner un produit</option>
          <option *ngFor="let product of products" [value]="product.id">{{ product.name }}</option>
        </select>
      </div>
      
      <div>
        <label class="block text-sm font-medium text-gray-700 mb-2">Quantité Réelle</label>
        <input 
          type="number" 
          [(ngModel)]="createForm.realQuantity"
          step="0.01"
          class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500">
      </div>
      
      <div>
        <label class="block text-sm font-medium text-gray-700 mb-2">Quantité Système</label>
        <input 
          type="number" 
          [(ngModel)]="createForm.systemQuantity"
          step="0.01"
          class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500">
      </div>
      
      <div>
        <label class="block text-sm font-medium text-gray-700 mb-2">Écart Calculé</label>
        <input 
          type="number" 
          [value]="calculateDiscrepancy()"
          readonly
          class="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50">
      </div>
      
      <div class="md:col-span-2">
        <label class="block text-sm font-medium text-gray-700 mb-2">Commentaire (optionnel)</label>
        <textarea 
          [(ngModel)]="createForm.comment"
          rows="3"
          class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="Commentaire sur l'écart..."></textarea>
      </div>
    </div>
    
    <div class="mt-6 flex justify-end space-x-3">
      <button 
        (click)="toggleCreateForm()"
        class="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 transition-colors duration-200">
        Annuler
      </button>
      <button 
        (click)="createDiscrepancy()"
        [disabled]="isLoading"
        class="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 transition-colors duration-200">
        <span *ngIf="isLoading" class="inline-block animate-spin mr-2">⟳</span>
        Créer l'Écart
      </button>
    </div>
  </div>

  <!-- Formulaire de création automatique -->
  <div class="bg-white rounded-lg shadow p-6 mb-8" *ngIf="showAutoForm">
    <h3 class="text-lg font-semibold text-gray-900 mb-4">Nouvel Écart Automatique</h3>
    <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div class="relative">
        <label class="block text-sm font-medium text-gray-700 mb-2">Produit</label>
        <input 
          type="text"
          [(ngModel)]="productSearchTerm"
          (input)="onProductSearchChange()"
          (focus)="onProductSearchFocus()"
          (blur)="hideProductDropdown()"
          placeholder="Rechercher un produit..."
          class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500">
        
        <!-- Dropdown pour les produits filtrés -->
        <div *ngIf="showProductDropdown && filteredProducts.length > 0" 
             class="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
          <div *ngFor="let product of filteredProducts" 
               (click)="selectProduct(product)"
               class="px-3 py-2 hover:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-b-0">
            <div class="font-medium text-gray-900">{{ product.name }}</div>
            <div class="text-sm text-gray-500">Code: {{ product.codeBare }}</div>
          </div>
        </div>
      </div>
      <div>
        <label class="block text-sm font-medium text-gray-700 mb-2">Quantité Réelle</label>
        <input 
          type="number" 
          [(ngModel)]="autoForm.realQuantity"
          name="autoRealQuantity"
          (input)="onAutoRealQuantityChange()"
          step="0.01"
          class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500">
      </div>
      <div>
        <label class="block text-sm font-medium text-gray-700 mb-2">Quantité Système</label>
        <input 
          type="number" 
          [value]="autoSystemQuantity"
          readonly disabled
          class="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50">
      </div>
      <div>
        <label class="block text-sm font-medium text-gray-700 mb-2">Écart Calculé</label>
        <input 
          type="number" 
          [value]="autoCalculatedDiscrepancy"
          readonly disabled
          class="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50">
      </div>
      <div class="md:col-span-2">
        <label class="block text-sm font-medium text-gray-700 mb-2">Commentaire (optionnel)</label>
        <textarea 
          [(ngModel)]="autoForm.comment"
          name="autoComment"
          rows="3"
          class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
          placeholder="Commentaire sur l'écart..."></textarea>
      </div>
    </div>
    <div class="mt-6 flex justify-end space-x-3">
      <button 
        (click)="toggleAutoForm()"
        class="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 transition-colors duration-200">
        Annuler
      </button>
      <button 
        type="button"
        (click)="createAutomaticDiscrepancy()"
        [disabled]="isLoading"
        class="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 transition-colors duration-200">
        <span *ngIf="isLoading" class="inline-block animate-spin mr-2">⟳</span>
        Créer l'Écart Automatique
      </button>
    </div>
  </div>

  <!-- Filtres -->
  <div class="bg-white rounded-lg shadow p-6 mb-8">
    <h3 class="text-lg font-semibold text-gray-900 mb-4">Filtres</h3>
    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      <div>
        <label class="block text-sm font-medium text-gray-700 mb-2">Statut</label>
        <select 
          [(ngModel)]="statusFilter"
          (change)="applyFilters()"
          class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500">
          <option value="all">Tous les statuts</option>
          <option value="En attente">En attente</option>
          <option value="Approuvé">Approuvé</option>
          <option value="Rejeté">Rejeté</option>
        </select>
      </div>
      
      <div class="relative">
        <label class="block text-sm font-medium text-gray-700 mb-2">Produit</label>
        <input 
          type="text"
          [(ngModel)]="filterProductSearchTerm"
          (input)="onFilterProductSearchChange()"
          (focus)="onFilterProductSearchFocus()"
          (blur)="hideFilterProductDropdown()"
          placeholder="Rechercher un produit..."
          class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500">
        
        <!-- Dropdown pour les produits filtrés -->
        <div *ngIf="showFilterProductDropdown && filteredFilterProducts.length > 0" 
             class="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
          <div *ngFor="let product of filteredFilterProducts" 
               (click)="selectFilterProduct(product)"
               class="px-3 py-2 hover:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-b-0">
            <div class="font-medium text-gray-900">{{ product.name }}</div>
            <div class="text-sm text-gray-500">Code: {{ product.codeBare }}</div>
          </div>
        </div>
      </div>

      <div>
        <label class="block text-sm font-medium text-gray-700 mb-2">Date</label>
        <input 
          type="date"
          [(ngModel)]="filterDate"
          (change)="applyFilters()"
          class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500">
      </div>

      <div class="flex gap-2 items-end">
        <button
          (click)="applyFilters()"
          class="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
          <svg class="w-4 h-4 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
          </svg>
          Appliquer
        </button>
        <button
          (click)="resetFilters()"
          class="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors">
          <svg class="w-4 h-4 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
          </svg>
          Réinitialiser
        </button>
      </div>
    </div>
  </div>

  <!-- Liste des écarts -->
  <div class="bg-white rounded-lg shadow overflow-hidden">
    <div class="px-6 py-4 border-b border-gray-200">
      <h3 class="text-lg font-semibold text-gray-900">Liste des Écarts de Stock</h3>
    </div>
    
    <div class="overflow-x-auto">
      <table class="min-w-full divide-y divide-gray-200">
        <thead class="bg-gray-50">
          <tr>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Produit</th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Quantité Réelle</th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Quantité Système</th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Écart</th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Statut</th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Créé par</th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
          </tr>
        </thead>
        <tbody class="bg-white divide-y divide-gray-200">
          <tr *ngFor="let discrepancy of getPaginatedDiscrepancies()" class="hover:bg-gray-50">
            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
              {{ formatDate(discrepancy.createdAt) }}
            </td>
            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
              {{ discrepancy.productName }}
            </td>
            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
              {{ formatQuantity(discrepancy.realQuantity) }}
            </td>
            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
              {{ formatQuantity(discrepancy.systemQuantity) }}
            </td>
            <td class="px-6 py-4 whitespace-nowrap text-sm font-medium" [ngClass]="getDiscrepancyClass(discrepancy.discrepancyQuantity)">
              {{ formatQuantity(discrepancy.discrepancyQuantity) }}
            </td>
            <td class="px-6 py-4 whitespace-nowrap">
              <span class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full" [ngClass]="getStatusClass(discrepancy.status)">
                {{ discrepancy.status }}
              </span>
            </td>
            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
              {{ discrepancy.createdByUsername || 'N/A' }}
            </td>
            <td class="px-6 py-4 whitespace-nowrap text-sm font-medium">
              <div class="flex space-x-2">
                <button 
                  *ngIf="isAdmin && discrepancy.status === 'En attente'"
                  (click)="approveDiscrepancy(discrepancy.id!)"
                  [disabled]="isLoading"
                  class="text-green-600 hover:text-green-900 disabled:opacity-50">
                  <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                </button>
                <button 
                  *ngIf="isAdmin && discrepancy.status === 'En attente'"
                  (click)="rejectDiscrepancy(discrepancy.id!)"
                  [disabled]="isLoading"
                  class="text-red-600 hover:text-red-900 disabled:opacity-50">
                  <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                  </svg>
                </button>
                <button 
                  *ngIf="isAdmin"
                  (click)="openEditForm(discrepancy)"
                  [disabled]="isLoading"
                  class="text-blue-600 hover:text-blue-900 disabled:opacity-50"
                  title="Modifier">
                  <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path>
                  </svg>
                </button>
                <button 
                  *ngIf="isAdmin"
                  (click)="deleteDiscrepancy(discrepancy.id!)"
                  [disabled]="isLoading"
                  class="text-gray-600 hover:text-gray-900 disabled:opacity-50">
                  <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                  </svg>
                </button>
              </div>
            </td>
          </tr>
          <tr *ngIf="getPaginatedDiscrepancies().length === 0">
            <td colspan="8" class="px-6 py-8 text-center text-gray-500">
              <div class="flex flex-col items-center">
                <svg class="w-12 h-12 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"></path>
                </svg>
                <p class="text-lg font-medium text-gray-900 mb-2">Aucun écart de stock trouvé</p>
                <p class="text-sm text-gray-600">Commencez par créer votre premier écart de stock</p>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    
    <!-- Paginator -->
    <div class="mt-6">
      <app-paginator
        [totalItems]="totalItems"
        [itemsPerPage]="itemsPerPage"
        [currentPage]="currentPage"
        [pages]="pages"
        [pagedItemsSize]="pagedItemsSize"
        (pageChange)="onPageChange($event)">
      </app-paginator>
    </div>
  </div>

  <!-- Loading -->
  <div *ngIf="isLoading" class="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full flex items-center justify-center">
    <div class="bg-white p-8 rounded-lg shadow-xl">
      <div class="flex items-center">
        <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span class="ml-3 text-gray-700">Chargement...</span>
      </div>
    </div>
  </div>
</div>

<!-- Edit Modal -->
<div *ngIf="showEditForm" class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
  <div class="w-full max-w-2xl bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden max-h-[90vh] flex flex-col">
    <!-- Header -->
    <div class="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gray-50">
      <div>
        <h2 class="text-xl font-semibold text-gray-900">Modifier l'Écart de Stock</h2>
        <p class="text-sm text-gray-500">Mettre à jour les quantités et le commentaire</p>
      </div>
      <button (click)="closeEditForm()" class="p-2 rounded-full text-gray-500 hover:bg-gray-200">
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
        </svg>
      </button>
    </div>

    <!-- Body -->
    <div class="px-6 py-5 space-y-5 overflow-y-auto">
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">Quantité Réelle</label>
          <input
            type="number"
            [(ngModel)]="editForm.realQuantity"
            class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="0">
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">Quantité Système</label>
          <input
            type="number"
            [(ngModel)]="editForm.systemQuantity"
            class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="0">
        </div>
      </div>

      <div>
        <label class="block text-sm font-medium text-gray-700 mb-2">Écart Calculé</label>
        <input
          type="number"
          [value]="calculateEditDiscrepancy()"
          readonly
          class="w-full px-4 py-3 border border-gray-200 rounded-lg bg-gray-50 text-gray-800">
      </div>

      <div>
        <label class="block text-sm font-medium text-gray-700 mb-2">Commentaire</label>
        <textarea
          [(ngModel)]="editForm.comment"
          rows="3"
          class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="Ajoutez des détails ou des notes..."></textarea>
      </div>
    </div>

    <!-- Footer -->
    <div class="px-6 py-4 bg-gray-50 border-t border-gray-200 flex flex-col sm:flex-row sm:justify-end gap-3">
      <button
        (click)="closeEditForm()"
        class="px-5 py-3 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100">
        Annuler
      </button>
      <button
        (click)="saveEditForm()"
        [disabled]="isLoading"
        class="px-5 py-3 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center">
        <span *ngIf="!isLoading" class="inline-flex items-center">
          <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
          </svg>
          Enregistrer
        </span>
        <span *ngIf="isLoading" class="inline-flex items-center">
          <svg class="w-5 h-5 mr-2 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          Enregistrement...
        </span>
      </button>
    </div>
  </div>
</div>

<!-- Confirmation Dialog -->
<app-confirmation-dialog
  [isVisible]="showDeleteConfirmation"
  title="Confirmer la suppression"
  message="Êtes-vous sûr de vouloir supprimer cet écart ? Cette action est irréversible."
  confirmText="Supprimer"
  cancelText="Annuler"
  type="danger"
  (confirm)="confirmDelete()"
  (cancel)="cancelDelete()"
  (close)="cancelDelete()">
</app-confirmation-dialog> 